/**
 * DashboardChartHook
 *
 * One hook for every chart on the Transaction Dashboard. The server sends only
 * data; the chart options live here, keyed by `data-chart-type`:
 *
 *   line  – daily volume per channel
 *   donut – channel mix / transaction type mix
 *
 * Dataset attributes:
 *   data-chart-type  "line" | "donut"
 *   data-chart-data  JSON array of rows
 *   data-chart-keys  JSON (line: [{key, name, colour}], donut: {angleKey, labelKey})
 *
 * The chart is rebuilt rather than patched on update: the period selector can
 * change the row count and the series shape at once, which AG Charts does not
 * reconcile cleanly. The same rebuild is what a theme flip rides on.
 */

import { chartTheme, labelColour, surfaceFill, watchTheme, stopThemeWatch } from "../base/chartTheme";

// Same palette the grid badges use, so a channel is one colour across the page.
// The outcome keys (approved/declined/failed) serve Transaction Monitoring,
// which plots outcomes rather than channels; they share this map because a
// series key resolves through it regardless of which page asked for the chart.
const PALETTE = {
  pos: "#0284C7",
  qr: "#7C3AED",
  ecom: "#DB2777",
  softpos: "#0D9488",
  approved: "#16A34A",
  declined: "#DC2626",
  failed: "#EA580C",
};

const DONUT_COLOURS = [
  "#0284C7", "#7C3AED", "#DB2777", "#16A34A", "#EA580C",
  "#0D9488", "#D97706", "#DC2626", "#6366F1", "#65A30D",
];

// Channel Mix labels map back onto PALETTE so a channel keeps one colour in both
// the donut and the line chart. Without this the donut colours by slice order,
// so adding a channel silently recolours the ones after it. Donuts with labels
// that are not channels (Transaction Type Mix: SALE / REFUND / VOID) fall through
// to the positional list.
const CHANNEL_KEY_BY_LABEL = {
  POS: "pos",
  SoftPOS: "softpos",
  QR: "qr",
  "E-Commerce": "ecom",
};

const DashboardChartHook = {
  mounted() {
    this.render();
    // Canvas colours are baked in at construction, so a theme flip needs a
    // rebuild rather than a repaint.
    watchTheme(this, this.render);
  },

  updated() {
    this.render();
  },

  destroyed() {
    stopThemeWatch(this);
    this.teardown();
  },

  teardown() {
    if (this.chart) {
      try {
        this.chart.destroy();
      } catch (_e) {
        // A chart whose container was already removed throws on destroy; the
        // node is going away regardless.
      }
      this.chart = null;
    }
  },

  render() {
    this.teardown();

    if (!window.agCharts) {
      console.error("DashboardChartHook: agCharts not found on window");
      return;
    }

    const data = this.parse(this.el.dataset.chartData, []);
    const keys = this.parse(this.el.dataset.chartKeys, {});

    if (!Array.isArray(data) || data.length === 0) {
      this.showEmpty();
      return;
    }

    const options =
      this.el.dataset.chartType === "donut"
        ? this.donutOptions(data, keys)
        : this.lineOptions(data, keys);

    try {
      this.chart = window.agCharts.AgCharts.create({
        container: this.el,
        ...options,
        ...chartTheme(),
      });
    } catch (e) {
      console.error("DashboardChartHook: failed to create chart", e);
      this.showEmpty("Chart failed to render");
    }
  },

  lineOptions(data, keys) {
    const series = (keys.series || []).map((s) => ({
      type: "line",
      xKey: "date",
      yKey: s.key,
      yName: s.name,
      stroke: PALETTE[s.key] || "#6B7280",
      strokeWidth: 2,
      marker: { enabled: data.length <= 31, size: 5, fill: PALETTE[s.key] || "#6B7280" },
      tooltip: { renderer: ({ datum, yKey, yName }) => ({ title: yName, content: `${datum.date}: ${datum[yKey]}` }) },
    }));

    return {
      data,
      series,
      axes: [
        { type: "category", position: "bottom", label: { rotation: data.length > 14 ? -45 : 0 } },
        { type: "number", position: "left", title: { text: "Transactions" } },
      ],
      legend: { position: "bottom", item: { marker: { shape: "circle" } } },
      padding: { top: 8, right: 12, bottom: 4, left: 4 },
    };
  },

  donutFills(data, labelKey) {
    return data.map((datum, i) => {
      const key = CHANNEL_KEY_BY_LABEL[datum[labelKey]];
      return (key && PALETTE[key]) || DONUT_COLOURS[i % DONUT_COLOURS.length];
    });
  },

  donutOptions(data, keys) {
    const labelKey = keys.labelKey || "label";

    return {
      data,
      series: [
        {
          type: "donut",
          angleKey: keys.angleKey || "count",
          calloutLabelKey: labelKey,
          innerRadiusRatio: 0.7,
          fills: this.donutFills(data, labelKey),
          strokes: [surfaceFill()],
          strokeWidth: 2,
          calloutLabel: { enabled: data.length <= 8 },
          innerLabels: keys.innerLabel
            ? [
                { text: keys.innerLabel.value, fontSize: 22, fontWeight: "bold", color: labelColour("strong") },
                { text: keys.innerLabel.label, fontSize: 12, color: labelColour("muted"), margin: 4 },
              ]
            : [],
        },
      ],
      legend: { position: "bottom", item: { marker: { shape: "circle" } } },
      padding: { top: 8, right: 8, bottom: 4, left: 8 },
    };
  },

  parse(raw, fallback) {
    try {
      return JSON.parse(raw || "null") ?? fallback;
    } catch (_e) {
      return fallback;
    }
  },

  showEmpty(message = "No transactions in this period") {
    this.el.innerHTML =
      `<div class="flex h-full min-h-[16rem] items-center justify-center text-sm text-gray-400">${message}</div>`;
  },
};

export default DashboardChartHook;
